import { useEffect, useState } from "react";
import {
  Checkbox,
  ColorPreset,
  FontWeight,
  P,
  PlainLink,
  ToggleVariant,
  TypePreset,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import { useFormContext, useWatch } from "react-hook-form";

import { SignupFormData, WebsiteLinks } from "../../../consts";

import { TrackingEvent } from "src/common/trackingEvents";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import {
  FEE_FREE_PROMO_TERMS_AND_CONDITIONS_LINK,
  FSB_MEMBER_DISCOUNT_TERMS_LINK,
} from "src/components/routes/SignUp/consts";
import { useI18n, UserLocale } from "src/technical-integrations/i18n";

interface TNCProps {
  isReferralSignup: boolean;
  checked: boolean;
  setChecked: (checked: boolean) => void;
  hasFsbMemberDiscount?: boolean;
  skipNoFeesTerms?: boolean;
}
export const TermsAndConditionsCheck: React.FC<TNCProps> = ({
  isReferralSignup,
  checked,
  setChecked,
  hasFsbMemberDiscount,
  skipNoFeesTerms,
}) => {
  const [isUS, setIsUS] = useState(false);
  const { sendEvent } = useSegment();
  const [locale] = useI18n();
  const { register, setValue } = useFormContext<SignupFormData>();

  const geoValue = useWatch({ name: "geo" });

  const sanctionsLink = () => {
    switch (locale) {
      case UserLocale.ES_ES:
        return WebsiteLinks.SANCTIONS_AGREEMENT_ES;
      case UserLocale.FR_FR:
        return WebsiteLinks.SANCTIONS_AGREEMENT_FR;
      case UserLocale.DE_DE:
        return WebsiteLinks.SANCTIONS_AGREEMENT_DE;
      default:
        return WebsiteLinks.SANCTIONS_AGREEMENT;
    }
  };

  useEffect(() => {
    if (geoValue === "US") {
      setIsUS(true);
      return;
    }

    setIsUS(false);
    return;
  }, [geoValue]);

  return (
    <Checkbox
      id="terms_and_conditions"
      variant={ToggleVariant.Compact}
      checked={checked}
      {...(register("terms_and_conditions"),
      {
        required: true,
        onChange: () => {
          setValue("terms_and_conditions", !checked);
          setChecked(!checked);
        },
      })}
    >
      <P
        preset={TypePreset.Body_01}
        color={ColorPreset.TextOnLight_02}
        weight={FontWeight.Normal}
      >
        <Trans id="optimised-sign-up.form.i-agree">I agree to the</Trans>{" "}
        {isReferralSignup ? (
          <>
            <PlainLink
              layout="inline"
              textDecoration="underline"
              href={WebsiteLinks.REFERRAL_SCHEME_TERMS}
              target="_blank"
              color={ColorPreset.BackgroundDark_01}
              onClick={() =>
                sendEvent(
                  TrackingEvent.SIGNUP_REFERRAL_SCHEME_TERMS_LINK_CLICKED
                )
              }
            >
              <Trans id="optimised-sign-up.form.referral-scheme-terms">
                referral scheme terms
              </Trans>
            </PlainLink>
            {", "}
          </>
        ) : null}
        <PlainLink
          layout="inline"
          textDecoration="underline"
          href={WebsiteLinks.TERMS_AND_CONDITIONS}
          target="_blank"
          color={ColorPreset.BackgroundDark_01}
        >
          <Trans id="optimised-sign-up.form.terms-and-conditions">
            terms of service and incorporated terms
          </Trans>
        </PlainLink>
        {!skipNoFeesTerms && (
          <>
            {", "}
            {hasFsbMemberDiscount ? (
              <PlainLink
                layout="inline"
                textDecoration="underline"
                href={FSB_MEMBER_DISCOUNT_TERMS_LINK}
                target="_blank"
                color={ColorPreset.BackgroundDark_01}
              >
                <Trans>discount terms</Trans>
              </PlainLink>
            ) : (
              <PlainLink
                layout="inline"
                textDecoration="underline"
                href={FEE_FREE_PROMO_TERMS_AND_CONDITIONS_LINK}
                target="_blank"
                color={ColorPreset.BackgroundDark_01}
                onClick={() =>
                  sendEvent(
                    TrackingEvent.SIGNUP_90D_FEE_FREE_TERMS_LINK_CLICKED
                  )
                }
              >
                <Trans id="optimised-sign-up.form.90d-no-fees-terms">
                  no fees terms
                </Trans>
              </PlainLink>
            )}
          </>
        )}
        {isUS && (
          <>
            {", "}
            <PlainLink
              layout="inline"
              textDecoration="underline"
              href={WebsiteLinks.ESIGN_TERMS}
              target="_blank"
              color={ColorPreset.BackgroundDark_01}
            >
              <Trans>e-sign terms</Trans>
            </PlainLink>
          </>
        )}
        {", "}
        <PlainLink
          layout="inline"
          textDecoration="underline"
          href={sanctionsLink()}
          target="_blank"
          color={ColorPreset.BackgroundDark_01}
        >
          <Trans>sanctions agreement</Trans>
        </PlainLink>{" "}
        <Trans id="optimised-sign-up.form.understand-gocardless">
          and understand GoCardless’
        </Trans>{" "}
        <PlainLink
          layout="inline"
          textDecoration="underline"
          href={WebsiteLinks.PRIVACY_POLICY}
          target="_blank"
          color={ColorPreset.BackgroundDark_01}
        >
          <Trans id="optimised-sign-up.form.privacy-policy">
            privacy notice
          </Trans>
        </PlainLink>
        .
      </P>
    </Checkbox>
  );
};
